import { forwardRef, useImperativeHandle, useRef, useEffect } from 'react'
import YouTube, { YouTubeProps, YouTubeEvent } from 'react-youtube'
import { usePlayer } from '../../../context/PlayerContext'

export interface YouTubePlayerRef {
  seekTo: (seconds: number) => void
  play: () => void
  pause: () => void
}

export const YouTubePlayer = forwardRef<YouTubePlayerRef>((_, ref) => {
  const {
    currentTrack,
    isPlaying,
    volume,
    play,
    pause,
    next,
    previous,
    setProgress,
    setDuration,
    autoSkip30,
    addToHistory,
    registerPlayerSeek,
    peekPendingSeekRatio,
    consumePendingSeekRatio,
  } = usePlayer()

  const playerRef = useRef<any>(null)
  const playerReadyRef = useRef(false)
  const lastVideoIdRef = useRef<string | null>(null)
  const progressIntervalRef = useRef<number | null>(null)
  const hasAddedToHistory = useRef(false)
  const hasAutoSkipped = useRef(false)
  const currentVideoIdRef = useRef<string | null>(currentTrack?.videoId ?? null)
  const seekSettlingRef = useRef<{ videoId: string; target: number; until: number } | null>(null)
  // Keep a ref in sync with isPlaying so callbacks always see the latest value
  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying
  currentVideoIdRef.current = currentTrack?.videoId ?? null

  const getPlayerVideoId = () => {
    try {
      return playerRef.current?.getVideoData?.()?.video_id ?? null
    } catch {
      return null
    }
  }

  const isPlayerOnCurrentVideo = () => {
    const expectedVideoId = currentVideoIdRef.current
    if (!expectedVideoId) return false
    return getPlayerVideoId() === expectedVideoId
  }

  // Capture the video ID used for the initial iframe mount.
  // We never change the `videoId` prop — instead we call loadVideoById /
  // cueVideoById imperatively so the iframe stays alive across track changes.
  // iOS Safari requires playVideo() to be called within a user gesture; if the
  // iframe remounts on every track change, onReady fires asynchronously and iOS
  // blocks the play call. A stable iframe means the player is always "ready"
  // when the play button is tapped.
  const stableVideoIdRef = useRef<string | null>(null)
  if (stableVideoIdRef.current === null && currentTrack) {
    stableVideoIdRef.current = currentTrack.videoId
  }

  // Reset per-track flags synchronously during render when videoId changes
  if (currentTrack?.videoId !== lastVideoIdRef.current) {
    lastVideoIdRef.current = currentTrack?.videoId ?? null
    hasAddedToHistory.current = false
    hasAutoSkipped.current = false
    // Do NOT reset playerReadyRef — the player stays mounted
  }

  const opts: YouTubeProps['opts'] = {
    height: '1',
    width: '1',
    playerVars: {
      autoplay: 1,   // lets YouTube buffer eagerly; we override in onReady
      controls: 0,
      disablekb: 1,
      fs: 0,
      modestbranding: 1,
      playsinline: 1,
    },
  }

  const onReady = (event: YouTubeEvent) => {
    playerRef.current = event.target
    playerReadyRef.current = true
    playerRef.current.setVolume(volume)

    // Honour the current play state.  On desktop autoplay:1 would start the
    // video immediately; pauseVideo() stops that.  On iOS autoplay is always
    // blocked by the browser so pauseVideo() is a no-op but harmless.
    if (isPlayingRef.current) {
      playerRef.current.playVideo()
    } else {
      playerRef.current.pauseVideo()
    }

    const dur = playerRef.current.getDuration()
    if (dur) setDuration(dur)
  }

  const onStateChange = (event: YouTubeEvent) => {
    const isCurrentVideo = isPlayerOnCurrentVideo()

    // 0 = ended
    if (event.data === 0 && isCurrentVideo) {
      next()
    }

    // 1 = playing
    if (event.data === 1 && playerRef.current && isCurrentVideo && currentTrack) {
      const dur = playerRef.current.getDuration()
      if (dur) setDuration(dur)

      if (currentTrack && !hasAddedToHistory.current) {
        addToHistory(currentTrack)
        hasAddedToHistory.current = true
      }

      if (autoSkip30 && dur && !hasAutoSkipped.current) {
        const startSeconds = dur * 0.3
        playerRef.current.seekTo(startSeconds, true)
        setProgress(startSeconds)
        seekSettlingRef.current = { videoId: currentTrack.videoId, target: startSeconds, until: Date.now() + 1200 }
        hasAutoSkipped.current = true
      } else if (dur) {
        const pendingRatio = consumePendingSeekRatio()
        if (pendingRatio !== null) {
          const startSeconds = dur * pendingRatio
          playerRef.current.seekTo(startSeconds, true)
          setProgress(startSeconds)
          seekSettlingRef.current = { videoId: currentTrack.videoId, target: startSeconds, until: Date.now() + 1200 }
        }
      }
    }
  }

  const onError = () => {
    next()
  }

  useImperativeHandle(ref, () => ({
    seekTo: (seconds: number) => {
      playerRef.current?.seekTo(seconds, true)
    },
    play: () => {
      playerRef.current?.playVideo()
    },
    pause: () => {
      playerRef.current?.pauseVideo()
    },
  }))

  useEffect(() => {
    registerPlayerSeek((seconds: number) => {
      playerRef.current?.seekTo(seconds, true)
    })

    return () => registerPlayerSeek(null)
  }, [registerPlayerSeek])

  // When the track changes, load the new video without touching the iframe.
  // loadVideoById starts playback; cueVideoById preloads silently.
  useEffect(() => {
    if (!playerRef.current || !playerReadyRef.current || !currentTrack) return
    if (isPlayingRef.current) {
      playerRef.current.loadVideoById(currentTrack.videoId)
    } else {
      playerRef.current.cueVideoById(currentTrack.videoId)
    }
  }, [currentTrack?.videoId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle play / pause state changes
  useEffect(() => {
    if (!playerRef.current || !playerReadyRef.current) return
    if (isPlaying) {
      playerRef.current.playVideo()
    } else {
      playerRef.current.pauseVideo()
    }
  }, [isPlaying])

  // Volume changes
  useEffect(() => {
    playerRef.current?.setVolume(volume)
  }, [volume])

  // Expose the current track and transport controls to supported lock screens.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return

    if (currentTrack) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.title || currentTrack.releaseTitle,
        artist: currentTrack.artist,
        album: currentTrack.releaseTitle,
        artwork: currentTrack.thumbnail
          ? [{ src: currentTrack.thumbnail }]
          : undefined,
      })
    } else {
      navigator.mediaSession.metadata = null
    }

    const setHandler = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler | null
    ) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler)
      } catch {
        // Browsers expose different subsets of the Media Session actions.
      }
    }

    setHandler('play', () => {
      playerRef.current?.playVideo()
      play()
    })
    setHandler('pause', () => {
      playerRef.current?.pauseVideo()
      pause()
    })
    setHandler('previoustrack', previous)
    setHandler('nexttrack', next)

    return () => {
      setHandler('play', null)
      setHandler('pause', null)
      setHandler('previoustrack', null)
      setHandler('nexttrack', null)
    }
  }, [currentTrack, next, pause, play, previous])

  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'
  }, [isPlaying])

  // Progress polling
  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
    if (isPlaying && playerRef.current) {
      progressIntervalRef.current = window.setInterval(() => {
        if (!isPlayerOnCurrentVideo()) return
        const pendingRatio = peekPendingSeekRatio()
        if (pendingRatio !== null) {
          const optimisticDuration = currentTrack?.duration || 0
          if (optimisticDuration > 0) {
            setProgress(optimisticDuration * pendingRatio)
          }
          return
        }
        const t = playerRef.current?.getCurrentTime() || 0
        const settling = seekSettlingRef.current
        if (
          settling &&
          settling.videoId === currentVideoIdRef.current &&
          Date.now() < settling.until &&
          t < Math.max(1, settling.target - 1)
        ) {
          setProgress(settling.target)
          return
        }
        if (settling && Date.now() >= settling.until) {
          seekSettlingRef.current = null
        }
        setProgress(t)
      }, 500)
    }
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current)
    }
  }, [isPlaying, currentTrack, peekPendingSeekRatio, setProgress])

  // Nothing to show until we have a track to load
  if (!currentTrack && !stableVideoIdRef.current) return null

  return (
    <YouTube
      videoId={stableVideoIdRef.current!}
      opts={opts}
      onReady={onReady}
      onStateChange={onStateChange}
      onError={onError}
    />
  )
})

YouTubePlayer.displayName = 'YouTubePlayer'
